import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const VerificationHistory = ({ onSelectVerification }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');

  const mockVerificationHistory = [
    {
      id: 'verify_001',
      credentialId: 'cred_edu_stanford_cs_2024',
      credentialType: 'EducationalCredential',
      issuer: 'Stanford University',
      subject: 'John Smith',
      timestamp: '2024-09-02T16:30:00Z',
      status: 'verified',
      confidence: 95,
      inputMethod: 'qr_scan',
      processingTime: 8500,
      stepsPassed: 7,
      stepsTotal: 7
    },
    {
      id: 'verify_002',
      credentialId: 'cred_prof_aws_cert_2024',
      credentialType: 'ProfessionalCertification',
      issuer: 'AWS Certification Authority',
      subject: 'Sarah Johnson',
      timestamp: '2024-09-02T14:15:00Z',
      status: 'verified',
      confidence: 92,
      inputMethod: 'manual_json',
      processingTime: 7200,
      stepsPassed: 7,
      stepsTotal: 7
    },
    {
      id: 'verify_003',
      credentialId: 'cred_health_license_2023',
      credentialType: 'ProfessionalLicense',
      issuer: 'Medical Board of California',
      subject: 'Dr. Michael Chen',
      timestamp: '2024-09-02T12:45:00Z',
      status: 'warning',
      confidence: 78,
      inputMethod: 'manual_url',
      processingTime: 9800,
      stepsPassed: 5,
      stepsTotal: 7
    },
    {
      id: 'verify_004',
      credentialId: 'cred_gov_passport_2022',
      credentialType: 'GovernmentID',
      issuer: 'US State Department',
      subject: 'Emily Rodriguez',
      timestamp: '2024-09-02T10:20:00Z',
      status: 'failed',
      confidence: 45,
      inputMethod: 'manual_id',
      processingTime: 5400,
      stepsPassed: 3,
      stepsTotal: 7
    },
    {
      id: 'verify_005',
      credentialId: 'cred_corp_employment_2024',
      credentialType: 'EmploymentCredential',
      issuer: 'TechCorp Inc.',
      subject: 'David Wilson',
      timestamp: '2024-09-01T18:30:00Z',
      status: 'verified',
      confidence: 88,
      inputMethod: 'qr_scan',
      processingTime: 6700,
      stepsPassed: 6,
      stepsTotal: 7
    },
    {
      id: 'verify_006',
      credentialId: 'cred_edu_mit_phd_2023',
      credentialType: 'EducationalCredential',
      issuer: 'Massachusetts Institute of Technology',
      subject: 'Dr. Lisa Park',
      timestamp: '2024-09-01T15:45:00Z',
      status: 'verified',
      confidence: 97,
      inputMethod: 'manual_json',
      processingTime: 8900,
      stepsPassed: 7,
      stepsTotal: 7
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'verified', label: 'Verified' },
    { value: 'warning', label: 'Warning' },
    { value: 'failed', label: 'Failed' }
  ];

  const sortOptions = [
    { value: 'timestamp', label: 'Date & Time' },
    { value: 'confidence', label: 'Confidence Score' },
    { value: 'credentialType', label: 'Credential Type' },
    { value: 'issuer', label: 'Issuer' },
    { value: 'processingTime', label: 'Processing Time' }
  ];

  const filteredAndSortedHistory = useMemo(() => {
    let filtered = mockVerificationHistory?.filter(item => {
      const matchesSearch = searchTerm === '' || 
        item?.credentialId?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        item?.issuer?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        item?.subject?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        item?.credentialType?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || item?.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortBy];
      let bValue = b?.[sortBy];
      
      if (sortBy === 'timestamp') {
        aValue = new Date(aValue)?.getTime();
        bValue = new Date(bValue)?.getTime();
      }
      
      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [searchTerm, statusFilter, sortBy, sortOrder]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified': return 'text-success';
      case 'warning': return 'text-warning';
      case 'failed': return 'text-destructive';
      default: return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified': return 'CheckCircle';
      case 'warning': return 'AlertTriangle';
      case 'failed': return 'XCircle';
      default: return 'HelpCircle';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'verified': return 'bg-success/10 text-success border-success/20';
      case 'warning': return 'bg-warning/10 text-warning border-warning/20';
      case 'failed': return 'bg-destructive/10 text-destructive border-destructive/20';
      default: return 'bg-muted text-muted-foreground border-border';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInputMethodLabel = (method) => {
    const labels = {
      'qr_scan': 'QR Scan',
      'manual_json': 'JSON Input',
      'manual_url': 'URL Input',
      'manual_id': 'ID Lookup'
    };
    return labels?.[method] || method;
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 90) return 'text-success';
    if (confidence >= 70) return 'text-warning';
    return 'text-destructive';
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Icon name="History" size={20} className="text-primary mr-2" />
            <h3 className="font-semibold text-foreground">Verification History</h3>
          </div>
          <div className="text-sm text-muted-foreground">
            {filteredAndSortedHistory?.length} of {mockVerificationHistory?.length} records
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            type="search"
            placeholder="Search credentials..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="col-span-1 md:col-span-2"
          />
          
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
          />
          
          <div className="flex space-x-2">
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="icon"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
            >
              <Icon name={sortOrder === 'asc' ? 'ArrowUp' : 'ArrowDown'} size={16} />
            </Button>
          </div>
        </div>
      </div>
      {/* History List */}
      <div className="divide-y divide-border max-h-96 overflow-y-auto">
        {filteredAndSortedHistory?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Search" size={48} className="text-muted-foreground/50 mx-auto mb-4" />
            <h4 className="font-medium text-foreground mb-2">No Results Found</h4>
            <p className="text-sm text-muted-foreground">
              Try adjusting your search terms or filters
            </p>
          </div>
        ) : (
          filteredAndSortedHistory?.map((item) => (
            <div
              key={item?.id}
              className="p-4 hover:bg-muted/50 cursor-pointer transition-colors"
              onClick={() => onSelectVerification?.(item)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3 flex-1">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    item?.status === 'verified' ? 'bg-success/10' :
                    item?.status === 'warning'? 'bg-warning/10' : 'bg-destructive/10'
                  }`}>
                    <Icon 
                      name={getStatusIcon(item?.status)} 
                      size={20} 
                      className={getStatusColor(item?.status)}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <h4 className="font-medium text-foreground truncate">
                        {item?.credentialType}
                      </h4>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
                        getStatusBadge(item?.status)
                      }`}>
                        {item?.status}
                      </span>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium">Issuer:</span> {item?.issuer}
                    </p>
                    
                    <p className="text-sm text-muted-foreground mb-2">
                      <span className="font-medium">Subject:</span> {item?.subject}
                    </p>
                    
                    <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                      <span>{formatDate(item?.timestamp)}</span>
                      <span>•</span>
                      <span>{getInputMethodLabel(item?.inputMethod)}</span>
                      <span>•</span>
                      <span>{item?.stepsPassed}/{item?.stepsTotal} steps</span>
                      <span>•</span>
                      <span>{Math.round(item?.processingTime / 1000)}s</span>
                    </div>
                  </div>
                </div>
                
                <div className="text-right ml-4">
                  <div className={`text-lg font-bold ${getConfidenceColor(item?.confidence)}`}>
                    {item?.confidence}%
                  </div>
                  <div className="text-xs text-muted-foreground">confidence</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Footer */}
      {filteredAndSortedHistory?.length > 0 && (
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              Showing {filteredAndSortedHistory?.length} verification{filteredAndSortedHistory?.length !== 1 ? 's' : ''}
            </div>
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                iconPosition="left"
              >
                Export History
              </Button>
              <Button
                variant="ghost"
                size="sm"
                iconName="Trash2"
                iconPosition="left"
              >
                Clear History
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationHistory;